import { useUser, useAdmin } from '@/utils/useUser';
import { supabase } from '@/utils/supabase-client';
import { useRouter } from 'next/router';
import DOMPurify from 'isomorphic-dompurify';
import Head from 'next/head';
import { ArrowLeftIcon, TrashIcon } from '@heroicons/react/24/outline';
import ShareButtons from "@/components/ShareButtons";
import { SEOMeta } from '@/templates/SEOMeta';

const decodeHtmlEntities = (html) => {
    const entities = {
        '&amp;': '&',
        '&lt;': '<',
        '&gt;': '>',
        '&quot;': '"',
        '&#39;': "'",
        '&nbsp;': ' '
    };

    return html.replace(/&amp;|&lt;|&gt;|&quot;|&#39;|&nbsp;/g, match => entities[match]);
};

const stripHtml = (html = '') =>
    html
        .replace(/<[^>]*>/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();

export default function BlogPost({ post, seoData }) {
    const { user } = useUser();
    const { isAdmin } = useAdmin();
    const router = useRouter();

    if (!post) {
        return (
            <>
                <Head>
                    <title>{seoData?.title || 'Article not found - Afivio'}</title>
                    <meta name="description" content={seoData?.description || 'The requested article was not found'} />
                    <meta name="robots" content="noindex,nofollow" />
                    <link rel="canonical" href={seoData?.url || 'https://afivio.com/blog'} />
                </Head>
                <div className="min-h-screen flex items-center justify-center">
                    <div className="text-center">
                        <h1 className="text-2xl font-bold mb-4">Article not found</h1>
                        <button
                            onClick={() => router.push('/blog')}
                            className="bg-blue-600 text-white px-4 py-2 rounded"
                        >
                            Back to blog
                        </button>
                    </div>
                </div>
            </>
        );
    }

    if (!post.is_public && !user) {
        return (
            <>
                <Head>
                    <title>{seoData?.title || 'Restricted article - Afivio'}</title>
                    <meta name="description" content="This article is available only for registered users." />
                    <meta name="robots" content="noindex,nofollow" />
                    <link rel="canonical" href={seoData?.url || 'https://afivio.com/blog'} />
                </Head>
                <div className="min-h-screen flex items-center justify-center">
                    <div className="text-center p-6 bg-white rounded-lg shadow-md">
                        <h1 className="text-2xl font-bold mb-4">Restricted access</h1>
                        <p className="mb-6">
                            This article is available only for registered users
                        </p>
                        <button
                            onClick={() => router.push('/signin')}
                            className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700"
                        >
                            Log in
                        </button>
                    </div>
                </div>
            </>
        );
    }

    let cleanHtml = post.content || '';
    cleanHtml = decodeHtmlEntities(cleanHtml);
    cleanHtml = DOMPurify.sanitize(cleanHtml);
    const articleBody = stripHtml(cleanHtml);
    const canonicalUrl = seoData?.url || `https://afivio.com/blog/${post.slug}`;
    const articleDescription = seoData?.description || post.excerpt || post.title || '';
    const articlePublishedAt = post.created_at ? new Date(post.created_at).toISOString() : undefined;
    const articleModifiedAt = post.updated_at ? new Date(post.updated_at).toISOString() : articlePublishedAt;
    const articleTags = typeof post.tags === 'string'
        ? post.tags.split(',').map((tag) => tag.trim()).filter(Boolean)
        : [];
    const categoryToRender = (() => {
        const c = post.category;
        if (c === null || c === undefined) return null;
        if (Array.isArray(c)) return c;
        if (typeof c === 'string') {
            try {
                const parsed = JSON.parse(c);
                if (Array.isArray(parsed)) return parsed;
            } catch (e) {
                // не JSON — повернемо як є (рядок)
            }
            return c;
        }
        return c;
    })();
    const articleSection = Array.isArray(categoryToRender)
        ? categoryToRender.join(', ')
        : (categoryToRender || undefined);
    const articleStructuredData = {
        '@context': 'https://schema.org',
        '@graph': [
            {
                '@type': 'BlogPosting',
                '@id': `${canonicalUrl}#blogposting`,
                mainEntityOfPage: canonicalUrl,
                headline: post.title,
                description: articleDescription,
                image: seoData?.image ? [seoData.image] : undefined,
                author: post.author
                    ? { '@type': 'Person', name: post.author }
                    : { '@type': 'Organization', name: 'Afivio' },
                publisher: {
                    '@type': 'Organization',
                    '@id': 'https://afivio.com/#organization',
                    name: 'Afivio',
                    logo: {
                        '@type': 'ImageObject',
                        url: 'https://afivio.com/logo_main.png'
                    }
                },
                datePublished: articlePublishedAt,
                dateModified: articleModifiedAt,
                articleSection,
                keywords: articleTags,
                wordCount: articleBody ? articleBody.split(' ').length : undefined
            },
            {
                '@type': 'BreadcrumbList',
                '@id': `${canonicalUrl}#breadcrumb`,
                itemListElement: [
                    {
                        '@type': 'ListItem',
                        position: 1,
                        name: 'Home',
                        item: 'https://afivio.com/'
                    },
                    {
                        '@type': 'ListItem',
                        position: 2,
                        name: 'Blog',
                        item: 'https://afivio.com/blog'
                    },
                    {
                        '@type': 'ListItem',
                        position: 3,
                        name: post.title,
                        item: canonicalUrl
                    }
                ]
            }
        ]
    };

    const handleDelete = async () => {
        if (confirm('Are you sure you want to delete this post?')) {
            const { error } = await supabase
                .from('posts')
                .delete()
                .eq('id', post.id);

            if (error) {
                alert('Error deleting post');
                console.error(error);
            } else {
                router.push('/blog');
            }
        }
    };

    return (
        <>
            <SEOMeta
                title={seoData.title}
                description={seoData.description}
                keywords={seoData.keywords}
                img={seoData.image}
                url={`/blog/${post.slug}`}
            />
            <Head>
                <meta key="ogType" property="og:type" content="article" />
                <meta name="twitter:site" content="@useafivio" />
                {articlePublishedAt && <meta property="article:published_time" content={articlePublishedAt} />}
                {articleModifiedAt && <meta property="article:modified_time" content={articleModifiedAt} />}
                {post.author && <meta property="article:author" content={post.author} />}
                {articleTags.map((tag) => (
                    <meta key={`article-tag-${tag}`} property="article:tag" content={tag} />
                ))}
                <script
                    type="application/ld+json"
                    dangerouslySetInnerHTML={{ __html: JSON.stringify(articleStructuredData) }}
                />
            </Head>

            <div className="mx-auto px-4 py-8 bg-gray-50 max-w-7xl">
                <button
                    onClick={() => router.push('/blog')}
                    className="flex items-center text-blue-600 hover:text-blue-800 mb-6"
                >
                    <ArrowLeftIcon className="w-4 h-4 mr-2" />
                    Back to blog
                </button>

                <div className="max-w-4xl mx-auto mb-8">
                    <div className="mb-10">
                        <ShareButtons
                            url={seoData?.url}
                            title={post.title}
                        />
                    </div>
                </div>
                {isAdmin && (
                    <button
                        onClick={handleDelete}
                        className="flex items-center bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700 mb-6"
                    >
                        <TrashIcon className="w-4 h-4 mr-2" />
                        Delete Post
                    </button>
                )}

                <div
                    className="prose prose-lg max-w-4xl mx-auto"
                    dangerouslySetInnerHTML={{ __html: cleanHtml }}
                />

                <div className="flex justify-center items-center space-x-4 text-sm text-gray-600 mt-8">
                    {post.author && (
                        <span>By: {post.author}</span>
                    )}
                    {post.reading_time && (
                        <span> Reading time: {post.reading_time} mins</span>
                    )}
                    {categoryToRender && (
                        Array.isArray(categoryToRender) ? (
                            categoryToRender.map((cat, index) => (
                                <span key={index} className="bg-gray-100 px-2 py-1 rounded">
                  {cat}
                </span>
                            ))
                        ) : (
                            <span className="bg-gray-100 px-2 py-1 rounded">
                {categoryToRender}
              </span>
                        )
                    )}
                </div>
                <div className="flex justify-center items-center space-x-4 text-sm text-gray-600 mt-2">
                    Published: {new Date(post.created_at).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                })}
                </div>
            </div>
        </>
    );
}

export async function getServerSideProps(context) {
    const { req, res, params } = context;
    const { slug } = params;
    const siteUrl = 'https://afivio.com';
    const currentUrl = `${siteUrl}/blog/${slug}`;

    // Перевіряємо, чи це бот соціальної мережі
    const userAgent = req.headers['user-agent'] || '';
    const isSocialBot = /facebookexternalhit|Twitterbot|LinkedInBot|WhatsApp|Pinterest/i.test(userAgent);

    // Якщо це бот, то не виконуємо запит до Supabase, бо ми обробимо його в API маршруті
    if (isSocialBot) {
        return {
            props: {
                post: null,
                seoData: null
            }
        };
    }

    const { data: post, error } = await supabase
        .from('posts')
        .select('*')
        .eq('slug', slug)
        .single();

    if (!post) {
        return {
            props: {
                post: null,
                seoData: {
                    title: 'Article not found - Afivio',
                    description: 'The requested article was not found',
                    image: `${siteUrl}/og.png`,
                    url: currentUrl,
                    keywords: 'Afivio, blog, article'
                }
            }
        };
    }

    const getAbsoluteImageUrl = (imagePath) => {
        if (!imagePath) return `${siteUrl}/og.png`;
        if (imagePath.startsWith('http')) return imagePath;
        return `${siteUrl}${imagePath}`;
    };

    const seoData = {
        title: post.title || 'Afivio: Affiliate Marketing & Referral Campaign Platform.',
        description: post.excerpt || post.description || post.title || 'Affiliate Marketing & Referral Campaign Platform.',
        image: getAbsoluteImageUrl(post.image),
        url: currentUrl,
        keywords: post.keywords || 'Afivio, marketing platform, referral campaign software, partner program management, SaaS affiliate, ecommerce referral program, create referral program'
    };

    // Public articles are identical for every visitor. Cache the SSR response
    // at the CDN so crawlers and repeat visits do not invoke this function.
    // Private posts deliberately remain uncacheable because access is user-specific.
    if (post.is_public) {
        res.setHeader('Cache-Control', 'public, s-maxage=604800, stale-while-revalidate=86400');
    }

    return {
        props: {
            post,
            seoData
        }
    };
}
